import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import PageHeader1 from "../../../components/common/PageHeader1";
import { Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { fetchUsers } from "../../../Redux/Reducers/UserRoleReducer";
// AuthContext से usePermissions हुक को इम्पोर्ट करें
import { usePermissions } from "../../../context/AuthContext";

const UserRole = () => {
  // can फंक्शन को usePermissions हुक से निकालें
  const { can } = usePermissions();
  const dispatch = useDispatch();
  const { users, loading, totalDocs } = useSelector(
    (state) => state.UserRoleReducer || {}
  );

  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterExcludeHiddenColumns: true,
    quickFilterValues: [""],
  });

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    dispatch(
      fetchUsers({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
      })
    );
  }, [dispatch, paginationModel.page, paginationModel.pageSize]);

  const handleClickOpen = (userId) => {
    setSelectedUserId(userId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUserId(null);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem("vedhvidhi-admin-token");
      const res = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/adminUser/${selectedUserId}`,
        {
          method: "DELETE",
          headers: { Authorization: token },
        }
      );

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Failed to delete user");

      toast.success("Admin user deleted successfully!");
      dispatch(
        fetchUsers({
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
        })
      );
    } catch (err) {
      toast.error(err.message || "Failed to delete user");
    } finally {
      setDeleting(false);
      handleClose();
    }
  };

  const rows = users?.map((user, index) => ({
    id: user._id,
    sono: paginationModel.page * paginationModel.pageSize + index + 1,
    name: user.fullName,
    phone: user.mobileNumber,
    email: user.email,
    role: user.role?.roleName || "N/A",
    createdAt: new Date(user.createdAt).toLocaleString(),
    updatedAt: new Date(user.updatedAt).toLocaleString(),
  }));

  const canEdit = can('user_management:edit');
  const canDelete = can('user_management:delete');

  const columns = [
    { field: "sono", headerName: "S.No", width: 100, sortable: false },
    { field: "name", headerName: "Name", width: 200 },
    { field: "phone", headerName: "Phone", width: 150 },
    { field: "email", headerName: "Email", width: 200 },
    { field: "role", headerName: "Role", width: 150 },
    { field: "createdAt", headerName: "Created At", width: 200 },
    { field: "updatedAt", headerName: "Updated At", width: 200 },
    ...(canEdit || canDelete
      ? [
        {
          field: "actions",
          headerName: "Actions",
          width: 130,
          sortable: false,
          filterable: false,
          renderCell: ({ row }) => (
            <Box display="flex" alignItems="center" gap={0.5}>
              {canEdit && (
                <Tooltip title="Edit Admin User">
                  <IconButton component={Link} to={`/user-role-edit/${row.id}`}>
                    <i className="icofont-edit text-info"></i>
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete Admin User">
                  <IconButton onClick={() => handleClickOpen(row.id)}>
                    <i className="icofont-ui-delete text-danger"></i>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ),
        },
      ]
      : []),
  ];


  return (
    <div className="body d-flex py-1">
      <Toaster />
      <div className="container-xxl">
        <PageHeader1
          righttitle="Add Admin User"
          link="/user-role-add"
          // **बदलाव: "Add Admin User" बटन को 'user_management:add' प्रिविलेज के आधार पर दिखाएं**
          routebutton={can('user_management:add')}
          pagetitle="Admin User Management"
        />
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <Box sx={{ width: 1 }}>
                  <Box sx={{ height: 500 }}>
                    <DataGrid
                      loading={loading}
                      rows={rows || []}
                      columns={columns}
                      getRowId={(row) => row.id}
                      disableColumnFilter
                      disableDensitySelector
                      slots={{ toolbar: GridToolbar }}
                      filterModel={filterModel}
                      onFilterModelChange={setFilterModel}
                      slotProps={{ toolbar: { showQuickFilter: true } }}
                      columnVisibilityModel={columnVisibilityModel}
                      onColumnVisibilityModelChange={setColumnVisibilityModel}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      pageSizeOptions={[10, 25, 50, 100]}
                      paginationMode="server"
                      rowCount={totalDocs || 0}
                      disableRowSelectionOnClick
                    />
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{"Delete Admin User"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this admin user? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            autoFocus
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserRole;